/**
 * Collapse the feed's one-row-per-code jobs into the visits they describe.
 *
 * A CER feed emits ONE ROW PER ACCREDITATION CODE, so a site carrying both a
 * solar and a battery inspection arrives as two jobs — one PVD, one BTY —
 * pointing at each other through `relatedCaseId`. ServiceM8 books that site as
 * ONE work order carrying both codes, because it is one trip to one address.
 *
 * Every surface that answers "how much work is there" therefore means VISITS,
 * not rows: the jobs grid, the map, the booking calendar. Read per-job they all
 * show the same site twice, which reads as duplicated data and — on the
 * calendar — as two appointments for one booking. Counting per code is still
 * right for billing and for CER paperwork, so both units are kept and the
 * grouping lives here rather than being re-derived, differently, per screen.
 */
import { splitSearchTokens } from './address-text.js';
import { AccreditationCodeSchema, inspectionTypeFromAccreditationCode } from './cer-portal.js';
/**
 * ServiceM8's code order: PV first. Every surface that names a combined visit
 * uses this, so the jobs grid, the import's ServiceM8 preview and the work
 * order itself all identify the same visit the same way round.
 */
export function sortAccreditationCodes(codes) {
    const rank = (code) => (code.toUpperCase().startsWith('PVD') ? 0 : 1);
    return [...codes].sort((left, right) => rank(left) - rank(right));
}
/** ServiceM8's canonical combo form: `"PVDxxxxxxx / BTYxxxxxxx"`, PV first. */
export function comboPurchaseOrderNumber(codes) {
    return sortAccreditationCodes(codes).join(' / ');
}
function isPvCode(jobId) {
    return jobId.toUpperCase().startsWith('PVD');
}
function accreditationCode(value) {
    if (!value)
        return undefined;
    const parsed = AccreditationCodeSchema.safeParse(value);
    return parsed.success ? parsed.data : undefined;
}
/**
 * The battery code on the PV half of a combo. Anything that is not a PVD job
 * naming a BTY code — a standalone solar job, a battery job, "Retrofit" —
 * is not that half.
 */
export function comboBatteryCode(job) {
    const own = accreditationCode(job.jobId);
    const related = accreditationCode(job.relatedCaseId);
    if (!own || !related)
        return undefined;
    if (inspectionTypeFromAccreditationCode(own) !== 'pv')
        return undefined;
    if (inspectionTypeFromAccreditationCode(related) !== 'battery')
        return undefined;
    return related;
}
/**
 * Which job a visit row opens for this search.
 *
 * The row is still the visit, led by the PV half. A query that names exactly
 * one accreditation code on the visit opens that half, so searching a battery
 * code does not land on the solar job. A blank query, an address, a ServiceM8
 * number, or a query that names both codes keeps opening the primary.
 */
export function visitOpenJob(visit, search) {
    const partner = visit.partner;
    if (!partner)
        return visit.primary;
    const tokens = new Set(splitSearchTokens(search));
    const halves = [visit.primary, partner].filter((job) => tokens.has(job.jobId.toLowerCase()));
    return halves.length === 1 ? halves[0] : visit.primary;
}
/**
 * Group jobs into visits, pairing the two halves of each combo.
 *
 * The `relatedCaseId` link is read as UNDIRECTED. Intake writes it only onto
 * rows it creates and never updates an existing job, so when one half of a pair
 * was already in the store the link exists on one side only — following it in a
 * single direction would leave that half looking like a standalone inspection
 * and list the site twice anyway.
 *
 * A link naming a job that isn't in `jobs` — filtered out by the caller, or
 * never imported — leaves its owner standing alone rather than dropping it.
 *
 * Visits appear in the order their first member appears in `jobs`, so a sorted
 * input keeps its order.
 */
export function groupJobsIntoVisits(jobs) {
    const byId = new Map(jobs.map((job) => [job.jobId, job]));
    // Reverse edges, for the half that carries no link of its own.
    const linkedFrom = new Map();
    for (const job of jobs) {
        if (job.relatedCaseId && !linkedFrom.has(job.relatedCaseId)) {
            linkedFrom.set(job.relatedCaseId, job);
        }
    }
    const claimed = new Set();
    const visits = [];
    for (const job of jobs) {
        if (claimed.has(job.jobId))
            continue;
        claimed.add(job.jobId);
        const candidate = (job.relatedCaseId ? byId.get(job.relatedCaseId) : undefined) ?? linkedFrom.get(job.jobId);
        const partner = candidate && candidate.jobId !== job.jobId && !claimed.has(candidate.jobId)
            ? candidate
            : undefined;
        if (partner)
            claimed.add(partner.jobId);
        if (!partner) {
            visits.push({
                visitId: job.jobId,
                primary: job,
                jobIds: [job.jobId],
                purchaseOrderNumber: job.jobId,
            });
            continue;
        }
        // PV leads, matching comboPurchaseOrderNumber and the ServiceM8 create
        // path. Two codes of the same kind cannot be paired by intake, so the
        // jobId tiebreak is only here to stay deterministic if one ever is.
        const primary = isPvCode(job.jobId)
            ? job
            : isPvCode(partner.jobId)
                ? partner
                : job.jobId <= partner.jobId
                    ? job
                    : partner;
        const other = primary === job ? partner : job;
        const jobIds = [primary.jobId, other.jobId];
        visits.push({
            visitId: primary.jobId,
            primary,
            partner: other,
            jobIds,
            purchaseOrderNumber: comboPurchaseOrderNumber(jobIds),
        });
    }
    return visits;
}
//# sourceMappingURL=visits.js.map